import React, { useState, useEffect } from 'react';
import { Mail, MapPin, Phone, Clock } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { ToastContainer, toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import 'react-toastify/dist/ReactToastify.css';
import Footer from '../components/ui/Footer';
import { Helmet } from 'react-helmet-async';

function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    interests: {
      ms4materials: false,
      ms4nature: false,
    },
  });
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    const timer = setTimeout(() => {
      setIsMapLoaded(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, [location]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prevData) => ({
        ...prevData,
        interests: {
          ...prevData.interests,
          [name]: checked,
        },
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    // Honeypot anti-spam : si rempli, on bloque l'envoi
    const honeypot = (
      form.querySelector('input[name="website"]') as HTMLInputElement
    )?.value;
    if (honeypot) {
      return;
    }
    const selectedInterests = Object.entries(formData.interests)
      .filter(([_, v]) => v)
      .map(([k]) => {
        if (k === 'ms4materials') return 'MS4Materials';
        if (k === 'ms4nature') return 'MS4Nature';
        return k;
      })
      .join(', ');

    Array.from(form.querySelectorAll('input[name="interests"]')).forEach(
      (input) => input.remove()
    );
    Array.from(form.querySelectorAll('input[name="time"]')).forEach((input) =>
      input.remove()
    );

    const interestsInput = document.createElement('input');
    interestsInput.type = 'hidden';
    interestsInput.name = 'interests';
    interestsInput.value = selectedInterests;
    form.appendChild(interestsInput);

    // Ajoute le champ time
    const timeInput = document.createElement('input');
    timeInput.type = 'hidden';
    timeInput.name = 'time';
    timeInput.value = new Date().toLocaleString();
    form.appendChild(timeInput);

    emailjs.sendForm('service_ms4all', 'template_bl6svim', form).then(
      () => {
        toast.success(t('contact.successMessage'), {
          position: 'top-center',
          style: {
            backgroundColor: '#1d4ed8',
            color: 'white',
            fontWeight: '500',
            borderRadius: '8px',
            fontSize: '1.1rem',
            boxShadow: '0 4px 24px 0 rgba(30,64,175,0.15)',
          },
          icon: () => <span style={{ fontSize: '1.5em' }}>📧</span>,
        });
        setFormData({
          name: '',
          email: '',
          message: '',
          interests: {
            ms4materials: false,
            ms4nature: false,
          },
        });
      },
      (error) => {
        alert(t('contact.errorMessage') + ': ' + JSON.stringify(error));
        interestsInput.remove();
        timeInput.remove();
      }
    );
  };

  const getInterestLabel = (k: string) => {
    if (k === 'ms4materials') return 'MS4Materials';
    if (k === 'ms4nature') return 'MS4Nature';
    return k;
  };

  const renderMultilineText = (value: string) => {
    const lines = value.split('<br />');
    return lines.map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < lines.length - 1 ? <br /> : null}
      </React.Fragment>
    ));
  };

  return (
    <>
      <Helmet>
        <title>{t('contact.metadata.title')}</title>
        <meta name="description" content={t('contact.metadata.description')} />
      </Helmet>
      <div className="min-h-screen bg-gray-50 pt-16">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 py-16 text-white">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h1 className="text-center text-4xl font-bold">
              {t('contact.title')}
            </h1>
            <p className="mx-auto mt-4 max-w-3xl text-center text-xl">
              {t('contact.subtitle')}
            </p>
            <div className="mt-8 flex justify-center">
              <a
                href="https://calendar.app.google/Hmv3ohtGtQmrtfaL8"
                target="_blank"
                rel="noopener noreferrer"
                className="flex transform items-center gap-2 rounded-lg border border-blue-500 bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:scale-105 hover:border-blue-300 hover:bg-blue-700 hover:shadow-xl md:px-8 md:py-3 md:text-base"
              >
                <span>{t('contact.bookCall')}</span>
                <span className="text-lg">→</span>
              </a>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-2">
            {/* Contact Form */}
            <div className="rounded-lg bg-white p-8 shadow-lg">
              <h2 className="mb-6 text-2xl font-bold">
                {t('contact.sendMessage')}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot anti-spam */}
                <input
                  type="text"
                  name="website"
                  style={{ display: 'none' }}
                  tabIndex={-1}
                  autoComplete="off"
                />
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    {t('contact.name')}*
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder={t('contact.name')}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    {t('contact.email')}*
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder={t('contact.email')}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t('contact.fieldsOfInterest')}
                  </label>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <input
                        id="ms4materials"
                        name="ms4materials"
                        type="checkbox"
                        checked={formData.interests.ms4materials}
                        onChange={handleChange}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label
                        htmlFor="ms4materials"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        <span>MS4Materials</span>
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="ms4nature"
                        name="ms4nature"
                        type="checkbox"
                        checked={formData.interests.ms4nature}
                        onChange={handleChange}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label
                        htmlFor="ms4nature"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        <span>MS4Nature</span>
                      </label>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    {t('contact.message')}*
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder={t('contact.message')}
                  />
                </div>
                <button
                  type="submit"
                  className="w-full rounded-md bg-blue-600 px-6 py-3 text-white transition-colors hover:bg-blue-700"
                >
                  {t('contact.sendMessageButton')}
                </button>
              </form>
            </div>

            {/* Contact Information and Map */}
            <div className="space-y-8">
              <div className="rounded-lg bg-white p-8 shadow-lg">
                <h2 className="mb-6 text-2xl font-bold">
                  {t('contact.contactInfo')}
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <MapPin className="mt-1 h-6 w-6 text-blue-600" />
                    <div>
                      <h3 className="font-semibold">{t('contact.address')}</h3>
                      <p className="text-gray-600">
                        {renderMultilineText(t('contact.addressValue'))}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <Mail className="mt-1 h-6 w-6 text-blue-600" />
                    <div>
                      <h3 className="font-semibold">{t('contact.email')}</h3>
                      <p className="text-gray-600">contact@ms4all.eu</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <Phone className="mt-1 h-6 w-6 text-blue-600" />
                    <div>
                      <h3 className="font-semibold">{t('contact.phone')}</h3>
                      <p className="text-gray-600">+33 (0)6 48 00 29 48</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <Clock className="mt-1 h-6 w-6 text-blue-600" />
                    <div>
                      <h3 className="font-semibold">
                        {t('contact.businessHours')}
                      </h3>
                      <p className="text-gray-600">
                        {renderMultilineText(t('contact.businessHoursValue'))}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="rounded-lg bg-white p-8 shadow-lg">
                <h2 className="mb-6 text-2xl font-bold">
                  {t('contact.location')}
                </h2>
                <div className="aspect-w-16 aspect-h-9">
                  {isMapLoaded ? (
                    <iframe
                      title="MS4ALL Location"
                      className="h-[400px] w-full rounded-lg border-0"
                      src="https://www.openstreetmap.org/export/embed.html?bbox=1.88417,47.88404,1.90417,47.90404&layer=mapnik&marker=47.89404,1.89417"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                    ></iframe>
                  ) : (
                    <div className="flex h-[400px] w-full items-center justify-center rounded-lg bg-gray-100">
                      <p className="text-gray-500">{t('contact.loadingMap')}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
        <ToastContainer
          position="top-center"
          autoClose={4000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
        />
      </div>
    </>
  );
}

emailjs.init({
  publicKey: 'FmfsYYM33swP0WHc3',
});

export default Contact;
